import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { recurringInvoicesApi, invoiceTemplatesApi, clientsApi, taxMasterApi, feesMasterApi, agencyConfigApi, productMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import type { CreateRecurringInvoiceRequest, LineItemRequest, InvoiceTemplate, TaxMasterListItem, FeesMasterListItem, FrequencyType, RecurringInvoiceStatus } from '@/types'
import { formatCurrency, formatDateForInput } from '@/lib/utils'
import { usePermissions } from '@/contexts/PermissionContext'

// List of Indian Union Territories
const UNION_TERRITORIES = [
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
]

const isUnionTerritory = (state: string | undefined): boolean => {
  if (!state) return false
  return UNION_TERRITORIES.some(ut => ut.toLowerCase() === state.toLowerCase())
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
]

const statusColors: Record<RecurringInvoiceStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  PAUSED: 'bg-yellow-100 text-yellow-800',
  COMPLETED: 'bg-blue-100 text-blue-800',
  CANCELLED: 'bg-gray-100 text-gray-500',
}

interface CreateRecurringInvoicePageProps {
  mode?: 'create' | 'view' | 'edit'
}

export default function CreateRecurringInvoicePage({ mode: propMode }: CreateRecurringInvoicePageProps = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { canAddEdit } = usePermissions()

  const isViewMode = propMode === 'view' || (!!id && propMode !== 'edit')
  const isEditMode = propMode === 'edit'
  const isCreateMode = !id && !propMode

  // Fetch templates (INVOICE type only)
  const { data: templates = [] } = useQuery({
    queryKey: ['billingTemplates', 'INVOICE'],
    queryFn: () => invoiceTemplatesApi.getAll('INVOICE'),
  })

  // Fetch default template (INVOICE type)
  const { data: defaultTemplate } = useQuery({
    queryKey: ['billingTemplates', 'INVOICE', 'default'],
    queryFn: () => invoiceTemplatesApi.getDefault('INVOICE'),
  })

  // Fetch clients
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.getAllLegacy,
  })

  // Fetch active taxes
  const { data: taxes = [] } = useQuery({
    queryKey: ['taxes', 'active'],
    queryFn: () => taxMasterApi.getAll(true),
  })

  // Fetch active fees
  const { data: feesList = [] } = useQuery({
    queryKey: ['fees', 'active'],
    queryFn: () => feesMasterApi.getAll(true),
  })

  // Fetch active products
  const { data: products = [] } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: () => productMasterApi.getAll(true),
  })

  // Fetch agency configuration
  const { data: agencyConfig } = useQuery({
    queryKey: ['agencyConfig'],
    queryFn: agencyConfigApi.get,
  })

  // Fetch existing recurring invoice if editing/viewing
  const { data: existingRecurring, isLoading: isLoadingRecurring } = useQuery({
    queryKey: ['recurring-invoice', id],
    queryFn: () => recurringInvoicesApi.getById(id!),
    enabled: !!id,
  })

  const [formData, setFormData] = useState<CreateRecurringInvoiceRequest>({
    client_id: '',
    recurring_name: '',
    frequency_type: 'MONTHLY',
    day_of_month: 1,
    start_date: formatDateForInput(new Date().toISOString()),
    auto_send: false,
    days_before_due: 30,
    currency: 'USD',
    discount_value: 0,
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
  })

  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null)
  const [selectedTax, setSelectedTax] = useState<TaxMasterListItem | null>(null)
  const [selectedFees, setSelectedFees] = useState<FeesMasterListItem | null>(null)

  const clientIdToFetch = formData.client_id || existingRecurring?.client_id || ''

  const { data: selectedClientDetails } = useQuery({
    queryKey: ['client', clientIdToFetch],
    queryFn: () => clientsApi.getById(clientIdToFetch),
    enabled: !!clientIdToFetch,
  })

  // Set default template when loaded
  useEffect(() => {
    if (defaultTemplate && !selectedTemplate && isCreateMode) {
      setSelectedTemplate(defaultTemplate)
      setFormData(prev => ({
        ...prev,
        template_id: defaultTemplate.template_id,
        notes: defaultTemplate.default_notes || prev.notes,
        payment_terms: defaultTemplate.default_payment_terms || prev.payment_terms,
      }))
    }
  }, [defaultTemplate, selectedTemplate, isCreateMode])

  // Populate form with existing recurring invoice data
  useEffect(() => {
    if (existingRecurring) {
      setFormData({
        template_id: existingRecurring.template_id,
        client_id: existingRecurring.client_id,
        recurring_name: existingRecurring.recurring_name,
        frequency_type: existingRecurring.frequency_type,
        day_of_week: existingRecurring.day_of_week,
        day_of_month: existingRecurring.day_of_month,
        month_of_quarter: existingRecurring.month_of_quarter,
        month_of_year: existingRecurring.month_of_year,
        start_date: formatDateForInput(existingRecurring.start_date),
        end_date: existingRecurring.end_date ? formatDateForInput(existingRecurring.end_date) : undefined,
        auto_send: existingRecurring.auto_send,
        days_before_due: existingRecurring.days_before_due,
        currency: existingRecurring.currency,
        tax_id: existingRecurring.tax_id,
        tax_rate: existingRecurring.tax_rate,
        is_gst_invoice: existingRecurring.is_gst_invoice,
        fees_id: existingRecurring.fees_id,
        discount_type: existingRecurring.discount_type,
        discount_value: existingRecurring.discount_value,
        notes: existingRecurring.notes,
        payment_terms: existingRecurring.payment_terms,
        line_items: existingRecurring.line_items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit: item.unit,
          tax_rate: item.tax_rate,
          discount_amount: item.discount_amount,
          sort_order: item.sort_order,
        })),
      })
      if (existingRecurring.template) {
        setSelectedTemplate(existingRecurring.template)
      }
      if (existingRecurring.tax_id && taxes.length > 0) {
        const tax = taxes.find(t => t.tax_id === existingRecurring.tax_id)
        if (tax) setSelectedTax(tax)
      }
      if (existingRecurring.fees_id && feesList.length > 0) {
        const fees = feesList.find(f => f.fees_id === existingRecurring.fees_id)
        if (fees) setSelectedFees(fees)
      }
    }
  }, [existingRecurring, taxes, feesList])

  // Calculate totals with GST and Fees logic
  const calculations = useMemo(() => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
    const discountAmount = formData.discount_value || 0
    const taxableAmount = subtotal - discountAmount

    let taxAmount = 0, cgstAmount = 0, sgstAmount = 0, igstAmount = 0, utgstAmount = 0
    let isGSTInvoice = false, taxDescription = ''

    if (selectedTax) {
      if (selectedTax.is_gst) {
        isGSTInvoice = true
        const agencyState = agencyConfig?.state
        const clientState = selectedClientDetails?.state
        const agencyIsUT = isUnionTerritory(agencyState)
        const clientIsUT = isUnionTerritory(clientState)

        if (agencyState && clientState && agencyState.toLowerCase() === clientState.toLowerCase()) {
          if (agencyIsUT && clientIsUT) {
            cgstAmount = taxableAmount * ((selectedTax.cgst_rate || 0) / 100)
            utgstAmount = taxableAmount * ((selectedTax.utgst_rate || 0) / 100)
            taxAmount = cgstAmount + utgstAmount
            taxDescription = `CGST (${selectedTax.cgst_rate}%) + UTGST (${selectedTax.utgst_rate}%)`
          } else {
            cgstAmount = taxableAmount * ((selectedTax.cgst_rate || 0) / 100)
            sgstAmount = taxableAmount * ((selectedTax.sgst_rate || 0) / 100)
            taxAmount = cgstAmount + sgstAmount
            taxDescription = `CGST (${selectedTax.cgst_rate}%) + SGST (${selectedTax.sgst_rate}%)`
          }
        } else {
          igstAmount = taxableAmount * ((selectedTax.igst_rate || 0) / 100)
          taxAmount = igstAmount
          taxDescription = `IGST (${selectedTax.igst_rate}%)`
        }
      } else {
        taxAmount = taxableAmount * ((selectedTax.tax_rate || 0) / 100)
        taxDescription = `${selectedTax.tax_name} (${selectedTax.tax_rate}%)`
      }
    }

    let feesAmount = 0, feesDescription = ''
    const amountForFees = subtotal + taxAmount - discountAmount

    if (selectedFees) {
      if (selectedFees.fees_type === 'PERCENTAGE') {
        feesAmount = amountForFees * ((selectedFees.fees_percentage || 0) / 100)
        feesDescription = `${selectedFees.fees_name} (${selectedFees.fees_percentage}%)`
      } else {
        feesAmount = selectedFees.fees_amount || 0
        feesDescription = `${selectedFees.fees_name} (Fixed)`
      }
    }

    const total = subtotal + taxAmount + feesAmount - discountAmount

    return { subtotal, taxAmount, discountAmount, feesAmount, total, cgstAmount, sgstAmount, igstAmount, utgstAmount, isGSTInvoice, taxDescription, feesDescription }
  }, [formData.line_items, formData.discount_value, selectedTax, selectedFees, agencyConfig?.state, selectedClientDetails?.state])

  const createMutation = useMutation({
    mutationFn: recurringInvoicesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
      toast.success(t('recurringInvoices.created'))
      navigate('/billing/recurring-invoices')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('recurringInvoices.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => recurringInvoicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['recurring-invoice', id] })
      toast.success(t('recurringInvoices.updated'))
      navigate(`/billing/recurring-invoices/${id}`)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('recurringInvoices.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.template_id === templateId)
    setSelectedTemplate(template || null)
    setFormData(prev => ({
      ...prev,
      template_id: templateId,
      notes: template?.default_notes || prev.notes,
      payment_terms: template?.default_payment_terms || prev.payment_terms,
    }))
  }

  const handleClientChange = (clientId: string) => {
    const client = clients.find(c => c.client_id === clientId)
    setFormData(prev => ({ ...prev, client_id: clientId, business_id: undefined, currency: client?.currency || prev.currency }))
  }

  const handleBusinessChange = (businessId: string) => {
    setFormData(prev => ({ ...prev, business_id: businessId === 'none' ? undefined : businessId }))
  }

  const handleTaxChange = (taxId: string) => {
    if (taxId === 'none') {
      setSelectedTax(null)
      setFormData(prev => ({ ...prev, tax_id: undefined, tax_rate: 0, is_gst_invoice: false }))
    } else {
      const tax = taxes.find(t => t.tax_id === taxId)
      setSelectedTax(tax || null)
      setFormData(prev => ({ ...prev, tax_id: taxId }))
    }
  }

  const handleFeesChange = (feesId: string) => {
    if (feesId === 'none') {
      setSelectedFees(null)
      setFormData(prev => ({ ...prev, fees_id: undefined }))
    } else {
      const fees = feesList.find(f => f.fees_id === feesId)
      setSelectedFees(fees || null)
      setFormData(prev => ({ ...prev, fees_id: feesId }))
    }
  }

  const handleFrequencyChange = (freq: FrequencyType) => {
    const updates: Partial<CreateRecurringInvoiceRequest> = { frequency_type: freq }
    // Reset frequency-specific fields
    if (freq === 'WEEKLY' || freq === 'BI_WEEKLY') {
      updates.day_of_week = 1 // Monday
      updates.day_of_month = undefined
      updates.month_of_quarter = undefined
      updates.month_of_year = undefined
    } else if (freq === 'MONTHLY') {
      updates.day_of_week = undefined
      updates.day_of_month = 1
      updates.month_of_quarter = undefined
      updates.month_of_year = undefined
    } else if (freq === 'QUARTERLY') {
      updates.day_of_week = undefined
      updates.day_of_month = 1
      updates.month_of_quarter = 1
      updates.month_of_year = undefined
    } else if (freq === 'YEARLY') {
      updates.day_of_week = undefined
      updates.day_of_month = 1
      updates.month_of_quarter = undefined
      updates.month_of_year = 1
    }
    setFormData(prev => ({ ...prev, ...updates }))
  }

  const handleLineItemChange = (index: number, field: keyof LineItemRequest, value: any) => {
    const newLineItems = [...formData.line_items]
    newLineItems[index] = { ...newLineItems[index], [field]: value }
    setFormData(prev => ({ ...prev, line_items: newLineItems }))
  }

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find(p => p.product_id === productId)
    if (product) {
      const newLineItems = [...formData.line_items]

      newLineItems[index] = {
        ...newLineItems[index],
        product_id: productId,
        description: product.description || product.name,
        unit_price: product.price,
        unit: product.unit,
        tax_rate: product.tax_rate || 0,
      }

      // Build updates for formData
      const formUpdates: Partial<typeof formData> = {
        line_items: newLineItems,
      }

      // If product has tax, set the invoice-level tax
      if (product.tax_id) {
        const tax = taxes.find(t => t.tax_id === product.tax_id)
        if (tax) {
          setSelectedTax(tax)
          formUpdates.tax_id = product.tax_id
          formUpdates.tax_rate = tax.tax_rate
          formUpdates.is_gst_invoice = tax.is_gst
        }
      }

      // If product has discount, set the invoice-level discount
      if (product.discount_type && product.discount_value) {
        formUpdates.discount_type = product.discount_type
        formUpdates.discount_value = product.discount_value
      }

      // If product has fees, set the invoice-level fees
      if (product.fees_id) {
        const fees = feesList.find(f => f.fees_id === product.fees_id)
        if (fees) {
          setSelectedFees(fees)
          formUpdates.fees_id = product.fees_id
        }
      }

      setFormData(prev => ({ ...prev, ...formUpdates }))
    } else if (productId === 'none') {
      const newLineItems = [...formData.line_items]
      newLineItems[index] = {
        ...newLineItems[index],
        product_id: undefined,
      }
      setFormData(prev => ({ ...prev, line_items: newLineItems }))
    }
  }

  const addLineItem = () => {
    setFormData(prev => ({ ...prev, line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0 }] }))
  }

  const removeLineItem = (index: number) => {
    if (formData.line_items.length > 1) {
      setFormData(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== index) }))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const formatDateToRFC3339 = (dateStr: string | undefined) => {
      if (!dateStr) return undefined
      if (dateStr.includes('T')) return dateStr
      return `${dateStr}T00:00:00Z`
    }

    const apiData = {
      ...formData,
      start_date: formatDateToRFC3339(formData.start_date)!,
      end_date: formData.end_date ? formatDateToRFC3339(formData.end_date) : undefined,
      tax_rate: selectedTax?.is_gst ? 0 : (selectedTax?.tax_rate || 0),
      is_gst_invoice: calculations.isGSTInvoice,
      fees_id: selectedFees?.fees_id,
    }

    if (isCreateMode) {
      createMutation.mutate(apiData)
    } else if (isEditMode && id) {
      updateMutation.mutate({ id, data: apiData })
    }
  }

  const isFormValid = () => {
    if (!formData.client_id || !formData.recurring_name || !formData.start_date || !formData.currency) return false
    if (formData.line_items.length === 0) return false
    if (!formData.line_items.every(item => item.description && item.quantity > 0 && item.unit_price >= 0)) return false
    // Validate frequency-specific fields
    if ((formData.frequency_type === 'WEEKLY' || formData.frequency_type === 'BI_WEEKLY') && formData.day_of_week === undefined) return false
    if ((formData.frequency_type === 'MONTHLY' || formData.frequency_type === 'QUARTERLY' || formData.frequency_type === 'YEARLY') && !formData.day_of_month) return false
    if (formData.frequency_type === 'QUARTERLY' && !formData.month_of_quarter) return false
    if (formData.frequency_type === 'YEARLY' && !formData.month_of_year) return false
    return true
  }

  if (id && isLoadingRecurring) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('recurringInvoices.loading')}</div>
      </div>
    )
  }

  const pageTitle = isCreateMode ? t('recurringInvoices.createRecurring') : (isEditMode ? t('recurringInvoices.editRecurring') : t('recurringInvoices.recurringDetails'))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/billing/recurring-invoices">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">{pageTitle}</h1>
            {existingRecurring && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-muted-foreground">{existingRecurring.recurring_name}</span>
                <Badge className={statusColors[existingRecurring.status]} variant="secondary">
                  {existingRecurring.status}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('recurringInvoices.basicInfo')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3 space-y-2">
              <Label htmlFor="recurring_name">{t('recurringInvoices.recurringName')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Input
                id="recurring_name"
                value={formData.recurring_name}
                onChange={(e) => setFormData(prev => ({ ...prev, recurring_name: e.target.value }))}
                placeholder={t('recurringInvoices.namePlaceholder')}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template">{t('recurringInvoices.invoiceTemplate')}</Label>
              <Select value={formData.template_id || ''} onValueChange={handleTemplateChange} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('recurringInvoices.selectTemplate')} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.template_id} value={template.template_id}>
                      {template.template_name} {template.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="client">{t('recurringInvoices.client')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Select value={formData.client_id} onValueChange={handleClientChange} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('recurringInvoices.selectClient')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="business">{t('recurringInvoices.business')}</Label>
              <Select
                value={formData.business_id || 'none'}
                onValueChange={handleBusinessChange}
                disabled={isViewMode || !formData.client_id || !selectedClientDetails?.businesses?.length}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('recurringInvoices.selectBusiness')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common.none')}</SelectItem>
                  {selectedClientDetails?.businesses?.map(business => (
                    <SelectItem key={business.business_id} value={business.business_id}>
                      {business.business_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">{t('recurringInvoices.currency')}</Label>
              <Select value={formData.currency} onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Frequency Settings Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('recurringInvoices.frequencySettings')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="frequency_type">{t('recurringInvoices.frequency')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Select value={formData.frequency_type} onValueChange={(v) => handleFrequencyChange(v as FrequencyType)} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WEEKLY">{t('recurringInvoices.weekly')}</SelectItem>
                  <SelectItem value="BI_WEEKLY">{t('recurringInvoices.biWeekly')}</SelectItem>
                  <SelectItem value="MONTHLY">{t('recurringInvoices.monthly')}</SelectItem>
                  <SelectItem value="QUARTERLY">{t('recurringInvoices.quarterly')}</SelectItem>
                  <SelectItem value="YEARLY">{t('recurringInvoices.yearly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Day of Week for Weekly/Bi-Weekly */}
            {(formData.frequency_type === 'WEEKLY' || formData.frequency_type === 'BI_WEEKLY') && (
              <div className="space-y-2">
                <Label htmlFor="day_of_week">{t('recurringInvoices.dayOfWeek')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
                <Select value={String(formData.day_of_week ?? '')} onValueChange={(v) => setFormData(prev => ({ ...prev, day_of_week: parseInt(v) }))} disabled={isViewMode}>
                  <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                    <SelectValue placeholder={t('recurringInvoices.selectDay')} />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map(day => (
                      <SelectItem key={day.value} value={String(day.value)}>{day.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Day of Month for Monthly/Quarterly/Yearly */}
            {(formData.frequency_type === 'MONTHLY' || formData.frequency_type === 'QUARTERLY' || formData.frequency_type === 'YEARLY') && (
              <div className="space-y-2">
                <Label htmlFor="day_of_month">{t('recurringInvoices.dayOfMonth')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
                <Select value={String(formData.day_of_month ?? '')} onValueChange={(v) => setFormData(prev => ({ ...prev, day_of_month: parseInt(v) }))} disabled={isViewMode}>
                  <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                    <SelectValue placeholder={t('recurringInvoices.selectDay')} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Month of Quarter for Quarterly */}
            {formData.frequency_type === 'QUARTERLY' && (
              <div className="space-y-2">
                <Label htmlFor="month_of_quarter">{t('recurringInvoices.monthInQuarter')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
                <Select value={String(formData.month_of_quarter ?? '')} onValueChange={(v) => setFormData(prev => ({ ...prev, month_of_quarter: parseInt(v) }))} disabled={isViewMode}>
                  <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                    <SelectValue placeholder={t('recurringInvoices.selectMonth')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t('recurringInvoices.firstMonth')}</SelectItem>
                    <SelectItem value="2">{t('recurringInvoices.secondMonth')}</SelectItem>
                    <SelectItem value="3">{t('recurringInvoices.thirdMonth')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Month of Year for Yearly */}
            {formData.frequency_type === 'YEARLY' && (
              <div className="space-y-2">
                <Label htmlFor="month_of_year">{t('recurringInvoices.month')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
                <Select value={String(formData.month_of_year ?? '')} onValueChange={(v) => setFormData(prev => ({ ...prev, month_of_year: parseInt(v) }))} disabled={isViewMode}>
                  <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                    <SelectValue placeholder={t('recurringInvoices.selectMonth')} />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(month => (
                      <SelectItem key={month.value} value={String(month.value)}>{month.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="start_date">{t('recurringInvoices.startDate')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">{t('recurringInvoices.endDate')}</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value || undefined }))}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="days_before_due">{t('recurringInvoices.daysUntilDue')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Input
                id="days_before_due"
                type="number"
                min="0"
                value={formData.days_before_due || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, days_before_due: parseInt(e.target.value) || 0 }))}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
              <p className="text-xs text-muted-foreground">{t('recurringInvoices.daysUntilDueHelp')}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4">
            <div className="space-y-0.5">
              <Label htmlFor="auto_send">{t('recurringInvoices.autoSend')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('recurringInvoices.autoSendHelp')}
              </p>
            </div>
            <Switch
              id="auto_send"
              checked={formData.auto_send}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, auto_send: checked }))}
              disabled={isViewMode}
            />
          </div>
        </div>

        {/* Line Items Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('recurringInvoices.lineItems')}</h3>
          <Table>
            <TableHeader>
              <TableRow>
                {!isViewMode && <TableHead className="w-[180px]">{t('recurringInvoices.product')}</TableHead>}
                <TableHead className="w-[35%]">{t('recurringInvoices.description')}</TableHead>
                <TableHead>{t('recurringInvoices.qty')}</TableHead>
                <TableHead>{t('recurringInvoices.unitPrice')}</TableHead>
                <TableHead className="text-right">{t('recurringInvoices.amount')}</TableHead>
                {!isViewMode && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {formData.line_items.map((item, index) => (
                <TableRow key={index}>
                  {!isViewMode && (
                    <TableCell>
                      <Select
                        value={item.product_id || 'none'}
                        onValueChange={(value) => handleProductSelect(index, value)}
                      >
                        <SelectTrigger className="w-[170px] bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                          <SelectValue placeholder={t('recurringInvoices.selectProduct')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('recurringInvoices.manualEntry')}</SelectItem>
                          {products.map((product) => (
                            <SelectItem key={product.product_id} value={product.product_id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  )}
                  <TableCell>
                    <Input
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      placeholder={t('recurringInvoices.itemDescription')}
                      disabled={isViewMode}
                      className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity || ''}
                      onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20 bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                      disabled={isViewMode}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price || ''}
                      onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-28 bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
                      disabled={isViewMode}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.quantity * item.unit_price, formData.currency)}
                  </TableCell>
                  {!isViewMode && (
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        disabled={formData.line_items.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!isViewMode && (
            <Button type="button" variant="outline" className="mt-4" onClick={addLineItem}>
              <Plus className="mr-2 h-4 w-4" />
              {t('recurringInvoices.addLineItem')}
            </Button>
          )}
        </div>

        {/* Notes & Terms Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('recurringInvoices.notesAndTerms')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="notes">{t('recurringInvoices.notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={t('recurringInvoices.notesPlaceholder')}
                rows={3}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_terms">{t('recurringInvoices.paymentTerms')}</Label>
              <Textarea
                id="payment_terms"
                value={formData.payment_terms || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))}
                placeholder={t('recurringInvoices.paymentTermsPlaceholder')}
                rows={3}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
          </div>
        </div>

        {/* Tax, Fees & Summary Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('recurringInvoices.invoiceSummary')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tax">{t('recurringInvoices.tax')}</Label>
              <Select value={selectedTax?.tax_id || 'none'} onValueChange={handleTaxChange} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('recurringInvoices.noTax')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('recurringInvoices.noTax')}</SelectItem>
                  {taxes.map(tax => (
                    <SelectItem key={tax.tax_id} value={tax.tax_id}>
                      {tax.tax_name} {tax.is_gst ? '(GST)' : `(${tax.tax_rate}%)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fees">{t('recurringInvoices.fees')}</Label>
              <Select value={selectedFees?.fees_id || 'none'} onValueChange={handleFeesChange} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                  <SelectValue placeholder={t('recurringInvoices.noFees')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('recurringInvoices.noFees')}</SelectItem>
                  {feesList.map(fees => (
                    <SelectItem key={fees.fees_id} value={fees.fees_id}>
                      {fees.fees_name} ({fees.fees_type === 'PERCENTAGE' ? `${fees.fees_percentage}%` : t('recurringInvoices.fixed')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="discount">{t('recurringInvoices.discount')}</Label>
              <Input
                id="discount"
                type="number"
                min="0"
                step="0.01"
                value={formData.discount_value || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                disabled={isViewMode}
                className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"
              />
            </div>
          </div>

          {/* Summary Card */}
          <Card className="md:max-w-md">
            <CardContent className="pt-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('recurringInvoices.subtotal')}</span>
                <span>{formatCurrency(calculations.subtotal, formData.currency)}</span>
              </div>
              {calculations.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>{t('recurringInvoices.discount')}</span>
                  <span>-{formatCurrency(calculations.discountAmount, formData.currency)}</span>
                </div>
              )}
              {calculations.taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span>{calculations.taxDescription || t('recurringInvoices.tax')}</span>
                  <span>{formatCurrency(calculations.taxAmount, formData.currency)}</span>
                </div>
              )}
              {calculations.feesAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span>{calculations.feesDescription || t('recurringInvoices.fees')}</span>
                  <span>{formatCurrency(calculations.feesAmount, formData.currency)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-bold">
                <span>{t('recurringInvoices.total')}</span>
                <span>{formatCurrency(calculations.total, formData.currency)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Statistics (View Mode) */}
          {isViewMode && existingRecurring && (
            <Card className="md:max-w-md">
              <CardHeader>
                <CardTitle>{t('recurringInvoices.statistics')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('recurringInvoices.invoicesGenerated')}</span>
                  <span className="font-medium">{existingRecurring.invoices_generated}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>{t('recurringInvoices.nextInvoiceDate')}</span>
                  <span className="font-medium">{new Date(existingRecurring.next_invoice_date).toLocaleDateString()}</span>
                </div>
                {existingRecurring.last_invoice_date && (
                  <div className="flex justify-between text-sm">
                    <span>{t('recurringInvoices.lastInvoiceDate')}</span>
                    <span className="font-medium">{new Date(existingRecurring.last_invoice_date).toLocaleDateString()}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Form Actions */}
        {!isViewMode && (
          <div className="flex justify-center gap-4 pt-4">
            <Link to="/billing/recurring-invoices">
              <Button type="button" variant="outline">
                {t('common.cancel')}
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={!isFormValid() || createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isCreateMode ? t('recurringInvoices.createRecurring') : t('recurringInvoices.updateRecurring')}
            </Button>
          </div>
        )}

        {isViewMode && existingRecurring && existingRecurring.status !== 'CANCELLED' && existingRecurring.status !== 'COMPLETED' && canAddEdit('RECURRING_INVOICES') && (
          <div className="flex justify-center pt-4">
            <Link to={`/billing/recurring-invoices/${id}/edit`}>
              <Button>{t('recurringInvoices.editRecurring')}</Button>
            </Link>
          </div>
        )}
      </form>
    </div>
  )
}

